import React, { useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import Autocomplete from '@mui/material/Autocomplete';
import TextField from '@mui/material/TextField';

export default function AccountAutocomplete({
                                                clientId,
                                                value,
                                                onPick,
                                                onChange,
                                                label = 'Compte',
                                                disabled,
                                                prefillLib = '',
                                            }) {
    const [search, setSearch] = useState('');
    const [debounced, setDebounced] = useState('');
    const [options, setOptions] = useState([]);
    const [loading, setLoading] = useState(false);
    const [labelByCode, setLabelByCode] = useState(() => ({})); // cache code -> libellé

    // debounce 250ms
    useEffect(() => {
        const id = setTimeout(() => setDebounced(search), 250);
        return () => clearTimeout(id);
    }, [search]);

    // fetch options (chiffres => qCompte, sinon qLib)
    useEffect(() => {
        if (!clientId) { setOptions([]); return; }

        const ctrl = new AbortController();
        (async () => {
            setLoading(true);
            try {
                const raw = (debounced || '').trim();
                const onlyDigits = /^\d+$/.test(raw);
                const params = {
                    limit: 20,
                    fields: 'compte,libelle',
                    numeric: 'true',
                    sort: 'compte',
                    order: 'asc',
                    mode: 'and',
                };
                if (onlyDigits && raw) params.qCompte = raw;
                else params.qLib = raw || prefillLib;

                const { data } = await axios.get(
                    `/api/comptable/clients/${clientId}/plan`,
                    { params, signal: ctrl.signal }
                );

                const items = Array.isArray(data) ? data : (data?.data ?? data?.items ?? data?.results ?? []);
                const mapped = (items || []).map(it => ({ code: String(it.compte ?? ''), label: String(it.libelle ?? '') }));

                if (!ctrl.signal.aborted) {
                    setOptions(mapped);
                    setLabelByCode(prev => {
                        const next = { ...prev };
                        for (const it of mapped) if (it.code && it.label && !next[it.code]) next[it.code] = it.label;
                        return next;
                    });
                }
            } catch {
                if (!ctrl.signal.aborted) setOptions([]);
            } finally {
                if (!ctrl.signal.aborted) setLoading(false);
            }
        })();

        return () => ctrl.abort();
    }, [clientId, debounced, prefillLib]);

    // garde l'affichage stable même si l'option sort de la liste
    const selectedOption = useMemo(() => {
        if (!value) return null;
        const inList = options.find(o => o.code === value);
        if (inList) return inList;
        return { code: value, label: labelByCode[value] ?? '' };
    }, [value, options, labelByCode]);

    // 👉 après sélection, le champ affiche SEULEMENT le code
    const getOptionLabel = (opt) => (opt?.code ? String(opt.code) : '');

    return (
        <Autocomplete
            disabled={disabled}
            size="small"
            options={options}
            loading={loading}
            value={selectedOption}
            filterOptions={(x) => x} // pas de filtrage client
            onChange={(_, opt) => {
                const code = opt?.code || null;
                const lib  = opt?.label || '';
                onChange?.(code);               // ne remonte que le code
                onPick?.({ code, label: lib }); // si besoin du libellé côté parent
                if (code) setLabelByCode(prev => (prev[code] ? prev : { ...prev, [code]: lib }));
            }}
            onInputChange={(_, v, reason) => {
                if (reason === 'input' || reason === 'clear') setSearch(v);
            }}
            getOptionLabel={getOptionLabel}
            isOptionEqualToValue={(o, v) => o.code === v.code}
            // 👉 la liste montre "code — libellé", mais le champ n'affiche que le code
            renderOption={(props, option) => (
                <li {...props}>
                    <span style={{ fontWeight: 600 }}>{option.code}</span>
                    {option.label ? <span style={{ marginLeft: 8, opacity: 0.7 }}>— {option.label}</span> : null}
                </li>
            )}
            renderInput={(params) => (
                <TextField
                    {...params}
                    label={label}
                    placeholder="code (401…) ou libellé (fournisseur…)"
                />
            )}
            ListboxProps={{ style: { maxHeight: 320 } }}
            sx={{ minWidth: 260 }}
        />
    );
}
